import { ArrowRight, Radio, Lock, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATS = [
  { label: "Operational Status", value: "ACTIVE", testid: "stat-status" },
  { label: "Rescue Split", value: "80/10/10", testid: "stat-split" },
  { label: "Security Tier", value: "25D", testid: "stat-tier" },
  { label: "Vault Assets", value: "01", testid: "stat-assets" },
];

export default function HeroDeck({ animate }) {
  return (
    <section
      className="relative grid grid-cols-1 lg:grid-cols-12 gap-6"
      data-testid="hero-deck"
    >
      {/* Main hero card */}
      <div
        className={`relative lg:col-span-8 rounded-2xl p-8 sm:p-12 glass tactical-corner overflow-hidden ${
          animate ? "animate-fade-up" : "opacity-0"
        }`}
        data-testid="hero-card-main"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-violet/10 blur-3xl" />
        <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-gold/10 blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse-violet" />
            <span className="font-mono text-[10px] tracking-[0.32em] text-gold/90 uppercase">
              Mobile Command Deck · Vol 0
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
            Your <span className="gold-text">Freedom</span>.
            <br />
            Your <span className="gold-text">Command</span>.
          </h1>

          <p className="mt-6 max-w-xl font-body text-base sm:text-lg text-white/65 leading-relaxed">
            The Sovereign Lifestyle Hub. A secured node engineered for clarity,
            stability, and rescue. Deployed with army-grade discipline, designed
            for the operator who refuses noise.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              data-testid="hero-cta-vault"
              className="bg-gold hover:bg-gold-soft text-obsidian font-mono text-[11px] tracking-[0.2em] uppercase h-11 px-6 rounded-full shadow-[0_8px_28px_-8px_rgba(212,175,55,0.6)]"
              onClick={() =>
                document
                  .getElementById("digital-vault")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Open the Vault <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              data-testid="hero-cta-dashboard"
              className="bg-transparent border-violet/40 text-violet-soft hover:bg-violet/10 hover:text-white font-mono text-[11px] tracking-[0.2em] uppercase h-11 px-6 rounded-full"
              onClick={() =>
                document
                  .getElementById("revenue-dashboard")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Revenue Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Side intel */}
      <div
        className={`lg:col-span-4 rounded-2xl p-6 glass relative ${
          animate ? "animate-fade-up" : "opacity-0"
        }`}
        style={{ animationDelay: "120ms" }}
        data-testid="hero-card-intel"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-violet" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-white/60 uppercase">
              Live Intel
            </span>
          </div>
          <span className="w-1.5 h-1.5 rounded-full bg-violet animate-pulse-violet" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className="relative rounded-md border border-white/8 bg-black/40 p-4 hover:border-gold/40 transition-colors duration-300"
            >
              <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-white/45">
                {s.label}
              </div>
              <div className="mt-1.5 font-mono text-xl text-gold tracking-tight">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white/50">
            <Lock className="w-3 h-3" />
            <span className="font-mono text-[9px] tracking-[0.28em] uppercase">
              AES-256 · Sealed
            </span>
          </div>
          <Compass className="w-3.5 h-3.5 text-gold/70" />
        </div>
      </div>
    </section>
  );
}
